/* eslint-disable @typescript-eslint/camelcase */
import {
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Button from 'components/Button';
import { TableCell, TableRow } from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import SearchIcon from '@material-ui/icons/Search';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import clsx from 'clsx';
import Loading from 'components/Loading';
import i18n from 'i18n';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  RolesEnum,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import { getAllCompaniesRequest } from 'modules/companies/actions';
import { ListCompanyDataProps } from 'modules/companies/interfaces';
import { getAllPartnersRequest } from 'modules/partners/actions';
import { ListPartnerDataProps } from 'modules/partners/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { useTableFilters } from 'hooks/useTableFilters';
import ApplicationState from 'store/interfaces';
import { inSuperAdminView, inAdminView, inOperatorView } from 'utils/role';
import Paginate from 'components/Pagination/Paginate';
import { getCompanyName } from 'utils/tsHelpers';
import { superUser } from 'utils/helpers';
import styles from './styles';
import { TableHead } from './TableComponents';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 12,
  search: '',
};
interface OwnProps {
  companyList: ListCompanyDataProps;
  companySelected: CompanyData | null;
  isLoading: boolean;
  open: 'company' | 'partner' | null;
  partnerList: ListPartnerDataProps;
  partnerSelected: PartnerData | null;
  permissions?: PermissionsData;
  role?: RolesEnum;
  getAllCompaniesRequest: (props) => Promise<void>;
  getAllPartnersRequest: (props) => void;
  onClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
  setListOpened: (value) => void;
}

const DialogList: React.FC<OwnProps> = ({
  companyList,
  companySelected,
  isLoading,
  open,
  partnerList,
  partnerSelected,
  permissions,
  role,
  getAllCompaniesRequest,
  getAllPartnersRequest,
  onClose,
  selectCompany,
  selectPartner,
  setListOpened,
}) => {
  const classes = useStyles();
  const {
    query,
    handleOrder,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters(initialQuery);
  const [selected, setSelected] = React.useState<{
    company?: string;
    partner?: string;
  }>({ company: companySelected?.id, partner: partnerSelected?.id });
  const [list, setList] = React.useState<{ company: any; partner: any }>({
    company: [],
    partner: [],
  });
  const history = useHistory();

  React.useEffect(() => {
    if (open === 'company') {
      getAllCompaniesRequest(query);
    }
  }, [getAllCompaniesRequest, open, query]);

  React.useEffect(() => {
    setList({
      company: companyList?.results || [],
      partner: partnerList?.results || [],
    });
  }, [companyList, partnerList, open, selected.company]);

  const handleClose = () => {
    setQuery(initialQuery);
    onClose();
  };

  const mapItem = item => {
    if (open === 'company') {
      return {
        id: item?.company_uuid,
        name: getCompanyName(item),
        shortName: getCompanyName(item),
      };
    }

    if (open === 'partner') {
      return {
        id: item?.partner_uuid,
        name: item?.name,
        shortName: item?.name,
        company: {
          id: item?.company_uuid,
          name: item?.company_name,
        },
      };
    }

    return item;
  };

  const selectItem = (item: CompanyData | PartnerData, redirect = false) => {
    if (open === 'company') {
      selectCompany(item);
      selectPartner(null);
      getAllPartnersRequest({ company_uuid: item?.id });
      setListOpened('partner');
    } else {
      handleClose();
      if ('company' in item && item?.company) {
        if (item?.company?.id !== companySelected?.id) {
          selectCompany({
            id: item.company.id,
            name: item.company.name,
            shortName: item.company.name,
          });
        }
      }

      selectPartner(item);
    }

    setQuery(initialQuery);

    setSelected({
      ...selected,
      [open || 'id']: item?.id,
    });

    if (redirect) history.push('/dashboard/partner');
  };

  React.useEffect(() => {
    if (open === 'partner') {
      getAllPartnersRequest({
        company_uuid: companySelected ? companySelected.id : '',
        query,
      });
    }
  }, [getAllPartnersRequest, companySelected, open, query]);

  const [page, setPage] = React.useState<number>(0);

  const handlePageChange = (page: number) => {
    setQuery(prev => ({ ...prev, offset: page * query.limit }));
    setPage(page);
  };

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: open === 'partner' ? 'name' : 'display_name',
      i18n: i18n.t('NAME_KEY'),
      order: true,
      hidden: false,
    },
    {
      key: 'id',
      i18n: 'ID',
    },
  ].filter(item => !item?.hidden);

  React.useEffect(() => {
    if (
      !permissions?.can_view_all_resources &&
      !companySelected &&
      companyList.results.length === 1 &&
      !superUser(role)
    ) {
      const {
        company_uuid: id,
        company_name: name,
        trading_name: shortName,
      } = companyList.results[0];

      selectCompany({
        id,
        name,
        shortName,
      });
    }
  }, [permissions, companyList.results, companySelected, role, selectCompany]);

  React.useEffect(() => {
    if (
      !permissions?.can_view_all_resources &&
      !isLoading &&
      !companySelected &&
      companyList.results.length > 0 &&
      !superUser(role)
    ) {
      const {
        company_uuid: id,
        company_name: name,
        trading_name: shortName,
      } = companyList.results[0];

      selectItem({
        id,
        name,
        shortName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyList.results, companySelected, role]);

  React.useEffect(() => {
    if (
      !permissions?.can_view_all_resources &&
      !isLoading &&
      !partnerSelected &&
      list.partner?.length > 0 &&
      companySelected
    ) {
      const {
        partner_uuid: id,
        name,
        company_name,
        company_uuid,
      } = list.partner?.[0];

      if (companySelected.id !== company_uuid) {
        selectCompany({
          id: company_uuid,
          name: company_name,
          shortName: company_name,
        });
      }
      selectItem({
        id,
        name,
        shortName: name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerSelected, list.partner]);

  const [isSearchLocked, setIsSearchLocked] = React.useState(false);

  const initiateSearch = () => {
    if (isSearchLocked || isLoading) return;
    setIsSearchLocked(true);

    handleSearch();

    setTimeout(() => {
      setIsSearchLocked(false);
    }, 150);
  };

  return (
    <Dialog
      fullWidth
      open={Boolean(open)}
      onClose={handleClose}
      className={classes.root}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <Grid
        container
        direction="column"
        className={clsx(classes.yellowHeader, {
          [classes.grayHeader]: inSuperAdminView(role, companySelected),
          [classes.greenHeader]: inAdminView(role, companySelected),
          [classes.blueHeader]: inOperatorView(role, companySelected),
        })}
      >
        <MuiDialogTitle disableTypography className={classes.wrapperTitle}>
          <Typography
            className={clsx(classes.modalTitle, {
              [classes.modalTitleSuperAdmin]: inSuperAdminView(
                role,
                companySelected
              ),
            })}
          >
            {i18n.t(
              open === 'company' ? 'CHOOSE_COMPANY_KEY' : 'CHOOSE_PARTNER_KEY'
            )}
          </Typography>
          <Grid>
            <IconButton
              className={clsx(classes.buttonClose, {
                [classes.buttonCloseSuperAdmin]: inSuperAdminView(
                  role,
                  companySelected
                ),
              })}
              aria-label="close"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </MuiDialogTitle>
      </Grid>
      <Grid container direction="column" className={classes.headerSearch}>
        <TextField
          id="outlined-search"
          label={
            <Typography className={classes.searchTitle}>
              {i18n.t(
                open === 'company' ? 'SEARCH_COMPANY_KEY' : 'SEARCH_PARTNER_KEY'
              )}
            </Typography>
          }
          variant="outlined"
          onChange={({ target }) => {
            setSearchValue(target.value);
          }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              initiateSearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon className={classes.searchBarIcon} />
              </InputAdornment>
            ),
            style: { height: 45 },
          }}
        />
      </Grid>
      <DialogContent className={classes.content}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableRow}>
              <TableCell style={{ width: 20 }}></TableCell>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {item?.i18n}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell align="center" colSpan={4}>
                  <Loading />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {open &&
                  list[open]?.map(mapItem).map(item => (
                    <TableRow
                      key={item?.id}
                      id={`option-list-${item?.id}`}
                      onClick={() =>
                        selectItem(item, permissions?.list_partner_dashboard)
                      }
                      className={clsx(
                        [classes.tableRow, classes.tableRowHover],
                        {
                          [classes.itemSelected]:
                            selected[open] === item?.id && companySelected,
                        }
                      )}
                    >
                      <TableCell className={classes.likeCell}>
                        {selected[open] === item?.id && companySelected && (
                          <ThumbUpIcon fontSize="small" />
                        )}
                      </TableCell>
                      <Tooltip
                        placement="top"
                        arrow
                        title={item?.name.length > 35 ? item?.name : ''}
                      >
                        <TableCell className={classes.nameCompany}>
                          {item.name}
                        </TableCell>
                      </Tooltip>

                      <TableCell className={classes.shortNameIDCompany}>
                        ID - {item?.id}
                      </TableCell>
                    </TableRow>
                  ))}
              </>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <Grid container justify="center" className={classes.pagination}>
        <ButtonGroup color="default">
          <Paginate
            count={open === 'company' ? companyList.count : partnerList.count}
            page={page}
            handlePageChange={handlePageChange}
            query={query}
          />
        </ButtonGroup>
      </Grid>
      <DialogActions className={classes.footer}>
        {open === 'partner' ? (
          <Grid className={classes.buttons}>
            <Button
              variant="outlined"
              id="confirm-select-partner"
              onClick={() => {
                setListOpened('company');
              }}
            >
              {i18n.t('BACK_KEY')}
            </Button>
            <Button
              variant="outlined"
              id="no-select-partner"
              onClick={() => {
                handleClose();

                if (!partnerSelected) {
                  selectPartner(null);
                }
              }}
            >
              {i18n.t('CLOSE_KEY')}
            </Button>
          </Grid>
        ) : (
          <Grid container justify="flex-end" className={classes.cancelButton}>
            <Button variant="outlined" id="cancel-button" onClick={onClose}>
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        )}
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, partnerSelected, data },
  companies: { companyList, isLoading: isCompanyLoading },
  partners: { partnerList, isLoading: isPartnerLoading },
}: ApplicationState) => ({
  companyList,
  partnerList,
  companySelected,
  partnerSelected,
  isLoading: isCompanyLoading || isPartnerLoading,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  selectCompany,
  selectPartner,
  getAllCompaniesRequest,
  getAllPartnersRequest,
})(DialogList);
